"use client";

// MKT-#10: cookie-based signed-in state (no aws-amplify). Previously used
// getCurrentUser() + Hub from aws-amplify, which pulled the whole Amplify
// bundle into MarketingNav -> into every public marketing page's initial JS
// (the eager-Amplify main-thread cost that blocked the hero LCP paint).
//
// The mag-portal-access cookie is the SSR mirror of the portal access token
// (set at sign-in, cleared at sign-out by AccessTokenCookieMirror), so its
// presence is an equivalent signal for NAV-STATE UX. The authoritative gate
// is always the server (isApprovedRequest) + the /api routes - this is
// display-only, exactly like useIsApproved. Sign-in/out on marketing routes
// happens via navigation, so we re-read on focus/visibility instead of a
// live Amplify Hub listener.

import { useEffect, useState } from "react";

export type AuthState = "loading" | "authed" | "unauthed";

const ACCESS_COOKIE = "mag-portal-access";

function hasAccessCookie(): boolean {
  if (typeof document === "undefined") return false;
  for (const raw of document.cookie.split(";")) {
    const t = raw.trim();
    if (t.startsWith(`${ACCESS_COOKIE}=`) && t.length > ACCESS_COOKIE.length + 1) {
      return true;
    }
  }
  return false;
}

export function useIsSignedIn(): AuthState {
  const [state, setState] = useState<AuthState>("loading");

  useEffect(() => {
    const read = () => setState(hasAccessCookie() ? "authed" : "unauthed");
    read();
    // Re-check when the tab regains focus / becomes visible (covers sign-in
    // or sign-out that happened via navigation or in another tab).
    window.addEventListener("focus", read);
    document.addEventListener("visibilitychange", read);
    return () => {
      window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", read);
    };
  }, []);

  return state;
}
